import { HttpErrorResponse, HttpEventType } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { MatLegacySnackBar as MatSnackBar } from '@angular/material/legacy-snack-bar';
import { Router } from '@angular/router';

import { Auth } from '@aws-amplify/auth';
import { Actions, createEffect, ofType } from '@ngrx/effects';
import { Store } from '@ngrx/store';
import { TranslateService } from '@ngx-translate/core';
import {
  Observable,
  catchError,
  exhaustMap,
  filter,
  from,
  map,
  of,
  switchMap,
  tap,
  withLatestFrom,
} from 'rxjs';

import {
  GetMerchantSitesResponse,
  ListMarketingDataViewModel,
  MarketingDataViewModel,
  MerchantBasicDataViewModel,
  MerchantContactsViewModel,
  MerchantResponse,
  MerchantSearchResponse,
  MerchantSiteViewModel,
  PageMerchantSearchResponse,
  PostMerchantSiteRequest,
  PutMerchantSiteRequest,
  ServedCustomerSegmentResponse,
} from '@app/api/models';
import { ContractsService, MerchantsService } from '@app/api/services';
import { ImpersonationActions } from '@app/easylot-admin/modules/impersonation/store/impersonation.slice';
import { OffersActions } from '@app/order-list/store/offers.slice';
import {
  AuthenticationActions,
  AuthenticationSelectors,
} from '@authentication/store/authentication.slice';
import { ImageUploadResponse } from '@image-upload/interfaces/image-upload-response.interface';
import { MerchantMarketingDataService } from '@merchant/services/merchant-marketing-data.service';
import {
  MerchantActions,
  MerchantSelectors,
} from '@merchant/store/merchant.slice';
import { SnackbarComponent } from '@shared/components/snackbar/snackbar.component';
import { PageResponse } from '@shared/interfaces/page-response.interface';
import { DialogService } from '@shared/services/dialog.service';
import { MerchantsWrapperService } from '@shared/services/merchants-wrapper.service';

@Injectable({ providedIn: 'root' })
export class MerchantEffects {
  /* Basic Data */

  loadBasicData$ = createEffect(() =>
    this.actions$.pipe(
      ofType(MerchantActions.loadBasicData.trigger),
      switchMap(() =>
        from(Auth.currentSession()).pipe(
          map((s) => {
            const impId = s.getIdToken().payload['custom:impMerchantId'];
            if (impId) {
              this.store.dispatch(
                ImpersonationActions.setMerchantId.trigger({
                  merchantId: impId,
                })
              );
              return impId as string;
            } else {
              return s.getIdToken().payload['custom:merchantId'] as string;
            }
          })
        )
      ),
      exhaustMap((merchantId) => {
        return this.merchantsService
          .getMerchantBasicData({ merchantId: merchantId })
          .pipe(
            map((response: MerchantBasicDataViewModel) =>
              MerchantActions.loadBasicData.success({
                data: response,
                merchantId: merchantId,
              })
            ),
            catchError(() => of(MerchantActions.loadBasicData.failure()))
          );
      })
    )
  );

  loadBasicDataById$ = createEffect(() =>
    this.actions$.pipe(
      ofType(MerchantActions.loadBasicDataById.trigger),
      withLatestFrom(
        this.store.select(AuthenticationSelectors.selectMerchantId)
      ),
      exhaustMap(([_, merchantId]) => {
        return this.merchantsService
          .getMerchantBasicData({ merchantId: merchantId })
          .pipe(
            map((response: MerchantBasicDataViewModel) =>
              MerchantActions.loadBasicData.success({
                data: response,
                merchantId: merchantId,
              })
            ),
            catchError(() => of(MerchantActions.loadBasicData.failure()))
          );
      })
    )
  );

  getMerchantById = createEffect(() =>
    this.actions$.pipe(
      ofType(MerchantActions.loadMerchantById.trigger),
      exhaustMap((action) => {
        return this.merchantsService
          .getMerchantById({ merchantId: action.id })
          .pipe(
            map((response: MerchantResponse) =>
              MerchantActions.loadMerchantById.success({
                data: response,
              })
            ),
            catchError(() => of(MerchantActions.loadMerchantById.failure()))
          );
      })
    )
  );

  updateBasicData$ = createEffect(() =>
    this.actions$.pipe(
      ofType(MerchantActions.updateBasicData.trigger),
      switchMap((action) =>
        from(Auth.currentSession()).pipe(
          map((s) => ({
            data: action.data,
            firstLogin: action.firstLogin,
            merchantId: (s.getIdToken().payload['custom:impMerchantId'] ??
              s.getIdToken().payload['custom:merchantId']) as string,
          }))
        )
      ),
      exhaustMap((action) => {
        return this.merchantsService
          .updateMerchantBasicData({
            merchantId: action.merchantId,
            body: action.data,
          })
          .pipe(
            map(() => {
              this.store.dispatch(MerchantActions.loadBasicData.trigger());
              if (action.firstLogin) {
                this.router.navigate(['/account/merchant/sites/create']);
              }
              return MerchantActions.updateBasicData.success();
            }),
            catchError((error: HttpErrorResponse) =>
              of(MerchantActions.updateBasicData.failure(error.error))
            )
          );
      })
    )
  );

  // Telesales step 3
  updateBasicDataByTelesales$ = createEffect(() =>
    this.actions$.pipe(
      ofType(MerchantActions.updateBasicDataByTelesales.trigger),
      withLatestFrom(
        this.store.select(AuthenticationSelectors.selectMerchantId)
      ),
      exhaustMap(([action, merchantId]) => {
        return this.merchantsService
          .updateMerchantBasicData({
            merchantId: merchantId,
            body: action.data,
          })
          .pipe(
            map(() => {
              this.store.dispatch(MerchantActions.loadBasicDataById.trigger());
              this.router.navigate(['/telesales/partner-site']);
              return MerchantActions.updateBasicDataByTelesales.success();
            }),
            catchError((error: HttpErrorResponse) =>
              of(
                MerchantActions.updateBasicDataByTelesales.failure(error.error)
              )
            )
          );
      })
    )
  );

  /* Basic Data - Logo */

  // TODO telesales logo?
  updateMerchantLogo$ = createEffect(() =>
    this.actions$.pipe(
      ofType(MerchantActions.updateMerchantLogo.trigger),
      switchMap((action) =>
        from(Auth.currentSession()).pipe(
          map((s) => ({
            data: action,
            merchantId: (s.getIdToken().payload['custom:impMerchantId'] ??
              s.getIdToken().payload['custom:merchantId']) as string,
          }))
        )
      ),
      exhaustMap((action) => {
        const formData: FormData = new FormData();
        formData.append('file', new Blob([action.data.arrayBuffer]), 'logo');
        return this.merchantsService
          .updateMerchantLogo({
            merchantId: action.merchantId,
            body: { file: formData.get('file') as Blob },
          })
          .pipe(
            map(() => MerchantActions.updateMerchantLogo.success()),
            catchError((error: HttpErrorResponse) =>
              of(MerchantActions.updateMerchantLogo.failure(error.error))
            )
          );
      })
    )
  );

  /* Contacts */

  loadContacts$ = createEffect(() =>
    this.actions$.pipe(
      ofType(MerchantActions.loadContacts.trigger),
      switchMap(() =>
        from(Auth.currentSession()).pipe(
          map(
            (s) =>
              (s.getIdToken().payload['custom:impMerchantId'] ??
                s.getIdToken().payload['custom:merchantId']) as string
          )
        )
      ),
      exhaustMap((merchantId) => {
        return this.merchantsService
          .listMerchantContact({ merchantId: merchantId })
          .pipe(
            map((response: MerchantContactsViewModel) =>
              MerchantActions.loadContacts.success(response)
            ),
            catchError(() => of(MerchantActions.loadContacts.failure()))
          );
      })
    )
  );

  updateContacts$ = createEffect(() =>
    this.actions$.pipe(
      ofType(MerchantActions.updateContacts.trigger),
      switchMap((action) =>
        from(Auth.currentSession()).pipe(
          map((s) => ({
            data: action,
            merchantId: (s.getIdToken().payload['custom:impMerchantId'] ??
              s.getIdToken().payload['custom:merchantId']) as string,
          }))
        )
      ),
      exhaustMap((action) => {
        return this.merchantsService
          .updateMerchantContact({
            merchantId: action.merchantId,
            body: action.data,
          })
          .pipe(
            map(() => MerchantActions.updateContacts.success(action.data)),
            catchError((error: HttpErrorResponse) =>
              of(MerchantActions.updateContacts.failure(error.error))
            )
          );
      })
    )
  );

  /* Sites */

  loadSites$ = createEffect(() =>
    this.actions$.pipe(
      ofType(MerchantActions.loadSites.trigger),
      switchMap(() =>
        from(Auth.currentSession()).pipe(
          map(
            (s) =>
              (s.getIdToken().payload['custom:impMerchantId'] ??
                s.getIdToken().payload['custom:merchantId']) as string
          )
        )
      ),
      exhaustMap((merchantId) => {
        return this.merchantsService
          .listMerchantSites1({ merchantId: merchantId })
          .pipe(
            map((response: GetMerchantSitesResponse) =>
              MerchantActions.loadSites.success(response)
            ),
            catchError(() => of(MerchantActions.loadSites.failure()))
          );
      })
    )
  );

  loadSitesByMerchantId$ = createEffect(() =>
    this.actions$.pipe(
      ofType(MerchantActions.loadSitesByMerchantId.trigger),
      exhaustMap((action) => {
        return this.merchantsService
          .listMerchantSites1({ merchantId: action.merchantId })
          .pipe(
            map((response: GetMerchantSitesResponse) =>
              MerchantActions.loadSitesByMerchantId.success(response)
            ),
            catchError(() =>
              of(MerchantActions.loadSitesByMerchantId.failure())
            )
          );
      })
    )
  );

  loadSiteSelectItems$ = createEffect(() =>
    this.actions$.pipe(
      ofType(MerchantActions.loadSiteSelectItems.trigger),
      exhaustMap(() =>
        this.merchantsService.getSiteSelectItemsOfMerchant().pipe(
          map((response) =>
            MerchantActions.loadSiteSelectItems.success({ response })
          ),
          catchError(() => of(MerchantActions.loadSites.failure()))
        )
      )
    )
  );

  createSite$ = createEffect(() =>
    this.actions$.pipe(
      ofType(MerchantActions.createSite.trigger),
      switchMap((action) =>
        from(Auth.currentSession()).pipe(
          map((s) => ({
            data: action.site,
            merchantId: (s.getIdToken().payload['custom:impMerchantId'] ??
              s.getIdToken().payload['custom:merchantId']) as string,
          }))
        )
      ),
      withLatestFrom(
        this.store.select(MerchantSelectors.selectSiteList),
        this.store.select(MerchantSelectors.selectOperatingLicenseInStore)
      ),
      exhaustMap(([action, siteList, operatingLicenseInStore]) =>
        this.merchantsService
          .createMerchantSites({
            merchantId: action.merchantId,
            body: action.data as PostMerchantSiteRequest,
          })
          .pipe(
            map((response: MerchantSiteViewModel | void) => {
              this.store.dispatch(MerchantActions.loadBasicData.trigger());
              this.store.dispatch(
                AuthenticationActions.getChoosableContexts.trigger()
              );

              if (operatingLicenseInStore) {
                this.store.dispatch(
                  MerchantActions.uploadOperatingLicense.trigger({
                    file: operatingLicenseInStore,
                    merchantId: action.merchantId,
                    siteId: (response as MerchantSiteViewModel).id,
                    notOpenSnackBar: true,
                  })
                );
              }

              return MerchantActions.createSite.success({
                response: response ?? action.data,
              });
            }),
            tap(() => {
              if (siteList?.length > 0) {
                this.router.navigate(['/account/merchant/sites']);
              } else if (siteList?.length === 0) {
                this.dialogService
                  .open({
                    type: 'NOTICE',
                    title: 'SHARED.NOTICE',
                    content: 'SHARED.DELIVERY_METHOD_NOTICE_TEXT',
                  })
                  .subscribe((result) => {
                    if (result) {
                      this.router.navigate(['/selling/delivery-mode/new']);
                    }
                  });
              }
            }),
            catchError((error: HttpErrorResponse) =>
              of(MerchantActions.createSite.failure(error.error))
            )
          )
      )
    )
  );

  // Telesales step 4
  createSiteByTelesales$ = createEffect(() =>
    this.actions$.pipe(
      ofType(MerchantActions.createSiteByTelesales.trigger),
      withLatestFrom(
        this.store.select(AuthenticationSelectors.selectMerchantId),
        this.store.select(MerchantSelectors.selectOperatingLicenseInStore)
      ),
      exhaustMap(([action, merchantId, operatingLicenseInStore]) =>
        this.merchantsService
          .createMerchantSites({
            merchantId: merchantId,
            body: action.site as PostMerchantSiteRequest,
          })
          .pipe(
            map((response: MerchantSiteViewModel | void) => {
              if (operatingLicenseInStore) {
                this.store.dispatch(
                  MerchantActions.uploadOperatingLicense.trigger({
                    file: operatingLicenseInStore,
                    merchantId: merchantId,
                    siteId: (response as MerchantSiteViewModel).id,
                    notOpenSnackBar: true,
                  })
                );
              }

              this.snackBar.openFromComponent(SnackbarComponent, {
                horizontalPosition: 'center',
                verticalPosition: 'bottom',
                duration: 3000,
                data: this.translateService.instant('SHARED.SAVE_SUCCESS'),
              });
              this.router.navigate(['/telesales']);
              return MerchantActions.createSiteByTelesales.success();
            }),
            catchError((error: HttpErrorResponse) =>
              of(MerchantActions.createSite.failure(error.error))
            )
          )
      )
    )
  );

  editSite$ = createEffect(() =>
    this.actions$.pipe(
      ofType(MerchantActions.editSite.trigger),
      switchMap((action) =>
        from(Auth.currentSession()).pipe(
          map((s) => ({
            data: action.site,
            merchantId: (s.getIdToken().payload['custom:impMerchantId'] ??
              s.getIdToken().payload['custom:merchantId']) as string,
          }))
        )
      ),
      exhaustMap((action) =>
        this.merchantsService
          .updateSite({
            merchantId: action.merchantId,
            siteId: action.data.id,
            body: action.data as PutMerchantSiteRequest,
          })
          .pipe(
            map((response: MerchantSiteViewModel | void) => {
              this.store.dispatch(MerchantActions.loadBasicData.trigger());
              this.store.dispatch(
                AuthenticationActions.getChoosableContexts.trigger()
              );
              return MerchantActions.editSite.success({
                response: response ?? action.data,
              });
            }),
            tap(() => this.router.navigate(['/account/merchant/sites'])),
            catchError((error: HttpErrorResponse) =>
              of(MerchantActions.editSite.failure(error.error))
            )
          )
      )
    )
  );

  deleteSite$ = createEffect(() =>
    this.actions$.pipe(
      ofType(MerchantActions.deleteSite.trigger),
      switchMap((action) =>
        from(Auth.currentSession()).pipe(
          map((s) => ({
            data: action,
            merchantId: (s.getIdToken().payload['custom:impMerchantId'] ??
              s.getIdToken().payload['custom:merchantId']) as string,
          }))
        )
      ),
      exhaustMap((action) => {
        return this.merchantsService
          .deleteSite({ merchantId: action.merchantId, siteId: action.data.id })
          .pipe(
            map(() => {
              this.store.dispatch(MerchantActions.loadBasicData.trigger());
              this.store.dispatch(
                AuthenticationActions.getChoosableContexts.trigger()
              );
              return MerchantActions.deleteSite.success({ id: action.data.id });
            }),
            catchError((_error: HttpErrorResponse) =>
              of(MerchantActions.deleteSite.failure())
            )
          );
      })
    )
  );

  loadServedCustomerSegments$ = createEffect(() =>
    this.actions$.pipe(
      ofType(MerchantActions.loadServedCustomerSegments.trigger),
      exhaustMap((action) =>
        this.merchantsService
          .calculateServedCustomerSegments({
            body: action.merchantTypes,
          })
          .pipe(
            map((resp: Array<ServedCustomerSegmentResponse>) =>
              MerchantActions.loadServedCustomerSegments.success({
                segments: resp,
              })
            )
          )
      )
    )
  );

  /* MarketingData */

  deleteMerchantProfileImage$ = createEffect(() =>
    this.actions$.pipe(
      ofType(MerchantActions.deleteMerchantProfileImage.trigger),
      exhaustMap((action) => {
        return this.marketingDataService
          .deleteMerchantProfileImage(action.id)
          .pipe(
            map(() => MerchantActions.deleteMerchantProfileImage.success()),
            catchError((error: HttpErrorResponse) =>
              of(
                MerchantActions.deleteMerchantProfileImage.failure(error.error)
              )
            )
          );
      })
    )
  );

  getMerchantProfileImage$ = createEffect(() =>
    this.actions$.pipe(
      ofType(MerchantActions.getMerchantProfileImage.trigger),
      exhaustMap((action) =>
        this.marketingDataService.getMerchantProfileImage(action.id).pipe(
          map((response: ImageUploadResponse) =>
            MerchantActions.getMerchantProfileImage.success(response)
          ),
          catchError((error: HttpErrorResponse) =>
            of(MerchantActions.getMerchantProfileImage.failure(error.error))
          )
        )
      )
    )
  );

  uploadMerchantProfileImage$ = createEffect(() =>
    this.actions$.pipe(
      ofType(MerchantActions.uploadMerchantProfileImage.trigger)
      // exhaustMap((action) =>
      //   this.marketingDataService.uploadMerchantProfileImage(action.data).pipe(
      //     map((response) =>
      //       MerchantActions.uploadMerchantProfileImage.success(response)
      //     ),
      //     catchError((error: HttpErrorResponse) =>
      //       of(MerchantActions.uploadMerchantProfileImage.failure(error.error))
      //     )
      //   )
      // )
    )
  );

  loadMarketingData$ = createEffect(() =>
    this.actions$.pipe(
      ofType(MerchantActions.loadMarketingData.trigger),
      exhaustMap((_action) =>
        from(Auth.currentSession()).pipe(
          map(
            (s) =>
              (s.getIdToken().payload['custom:impMerchantId'] ??
                s.getIdToken().payload['custom:merchantId']) as string
          ),
          exhaustMap((merchantId: string) =>
            this.merchantsService
              .getAllMarketingDataByMerchant({ merchantId: merchantId })
              .pipe(
                map((response: ListMarketingDataViewModel) =>
                  MerchantActions.loadMarketingData.success({
                    ...response,
                    marketingData: (response.marketingData ??
                      {}) as Array<MarketingDataViewModel>,
                  })
                ),
                catchError(() =>
                  of(MerchantActions.loadMarketingData.failure())
                )
              )
          )
        )
      )
    )
  );

  editMarketingData$ = createEffect(() =>
    this.actions$.pipe(
      ofType(MerchantActions.editMarketingData.trigger),
      exhaustMap((action) =>
        from(Auth.currentSession()).pipe(
          map(
            (s) =>
              (s.getIdToken().payload['custom:impMerchantId'] ??
                s.getIdToken().payload['custom:merchantId']) as string
          ),
          exhaustMap((merchantId: string) => {
            let operation: Observable<MarketingDataViewModel | void>;
            if (action?.id != null) {
              // PUT
              operation = this.merchantsService.updateMarketingDataByMerchant({
                merchantId: merchantId,
                marketingDataId: action.id,
                body: action,
              });
            } else {
              // POST
              operation = this.merchantsService.createMarketingDataToMerchant({
                merchantId: merchantId,
                body: action,
              });
            }
            return operation.pipe(
              map((response: MarketingDataViewModel | void) =>
                MerchantActions.editMarketingData.success(
                  (response ?? action) as MarketingDataViewModel
                )
              ),
              tap(() =>
                this.router.navigate(['/account/merchant/marketing-data'])
              ),
              catchError((error: HttpErrorResponse) =>
                of(MerchantActions.editMarketingData.failure(error.error))
              )
            );
          })
        )
      )
    )
  );

  deleteMarketingData$ = createEffect(() =>
    this.actions$.pipe(
      ofType(MerchantActions.deleteMarketingData.trigger),
      switchMap((action) =>
        from(Auth.currentSession()).pipe(
          map((s) => ({
            data: action,
            merchantId: (s.getIdToken().payload['custom:impMerchantId'] ??
              s.getIdToken().payload['custom:merchantId']) as string,
          }))
        )
      ),
      exhaustMap((action) => {
        return this.merchantsService
          .deleteMarketingDataByMerchant({
            merchantId: action.merchantId,
            marketingDataId: action.data.id,
          })
          .pipe(
            map(() => MerchantActions.deleteMarketingData.success(action.data)),
            catchError((_error: HttpErrorResponse) =>
              of(MerchantActions.deleteMarketingData.failure())
            )
          );
      })
    )
  );

  searchPartners$ = createEffect(() =>
    this.actions$.pipe(
      ofType(MerchantActions.searchPartners.trigger),
      withLatestFrom(this.store.select(MerchantSelectors.selectPartnersFilter)),
      switchMap(([_action, filter]) => {
        const params = {
          page: filter.page,
          size: filter.size,
          sort: filter.sort,
        };
        const { page: _, size: __, sort: ___, ...body } = filter;
        return this.merchantsService.searchMerchant({ ...params, body }).pipe(
          map((response: PageMerchantSearchResponse) =>
            MerchantActions.searchPartners.success({
              response: response as PageResponse<MerchantSearchResponse>,
            })
          ),
          catchError(() => of(MerchantActions.searchPartners.failure()))
        );
      })
    )
  );

  acceptContract$ = createEffect(() =>
    this.actions$.pipe(
      ofType(MerchantActions.acceptContract.trigger),
      switchMap((action) =>
        this.contractsService
          .accept({
            'If-Match': action.request.hash,
            merchantId: action.request.merchantId,
          })
          .pipe(
            map(() => {
              this.store.dispatch(
                OffersActions.acceptMerchantsContract.trigger({
                  request: { merchantId: action.request.merchantId },
                })
              );
              return MerchantActions.acceptContract.success();
            }),
            catchError(() => of(MerchantActions.acceptContract.failure()))
          )
      )
    )
  );

  loadOperatingLicense$ = createEffect(() =>
    this.actions$.pipe(
      ofType(MerchantActions.loadOperatingLicense.trigger),
      switchMap((action) =>
        this.merchantsService
          .getOperatingLicense$Response({
            merchantId: action.merchantId,
            siteId: action.siteId,
          })
          .pipe(
            map((response) => {
              const blob = new Blob([response.body as BlobPart], {
                type: response.headers
                  .get('content-disposition')
                  ?.includes('pdf')
                  ? 'application/pdf'
                  : 'image/jpg',
              });
              const url = window.URL.createObjectURL(blob);

              return MerchantActions.loadOperatingLicense.success({
                file: !!response.body ? url : null,
              });
            })
          )
      )
    )
  );

  uploadOperatingLicense$ = createEffect(() =>
    this.actions$.pipe(
      ofType(MerchantActions.uploadOperatingLicense.trigger),
      switchMap((action) => {
        const formData: FormData = new FormData();
        formData.append('file', new Blob([action.file]), action.file.name);

        return this.merchantsWrapperService
          .uploadOperatingLicense$Response({
            merchantId: action.merchantId,
            siteId: action.siteId,
            body: {
              file: formData.get('file') as Blob,
            },
          })
          .pipe(
            filter(
              (event: any) =>
                event.type === HttpEventType.UploadProgress ||
                event.type === HttpEventType.Response
            ),
            tap((event: any) => {
              if (event.type === HttpEventType.UploadProgress) {
                const progress = Math.round((100 / event.total) * event.loaded);
                this.store.dispatch(
                  MerchantActions.uploadOperatingLicense.progress({ progress })
                );
              }
            }),
            filter((event: any) => event.type === HttpEventType.Response),
            map((event: any) => {
              if (!action.notOpenSnackBar) {
                this.snackBar.openFromComponent(SnackbarComponent, {
                  horizontalPosition: 'center',
                  verticalPosition: 'bottom',
                  duration: 3000,
                  data: this.translateService.instant(
                    'SHARED.UPLOAD_OPERATING_LICENSE_SUCCESS'
                  ),
                });
              }
              this.store.dispatch(
                MerchantActions.loadOperatingLicense.trigger({
                  merchantId: action.merchantId,
                  siteId: action.siteId,
                })
              );
              return MerchantActions.uploadOperatingLicense.success();
            }),
            catchError(() =>
              of(MerchantActions.uploadOperatingLicense.failure())
            )
          );
      })
    )
  );

  constructor(
    private readonly actions$: Actions,
    private readonly router: Router,
    private readonly merchantsService: MerchantsService,
    private readonly merchantsWrapperService: MerchantsWrapperService,
    private readonly marketingDataService: MerchantMarketingDataService,
    private readonly store: Store,
    private readonly snackBar: MatSnackBar,
    private translateService: TranslateService,
    private readonly dialogService: DialogService,
    private readonly contractsService: ContractsService
  ) {}
}
